import {
  Color,
  DirectionalLight,
  EquirectangularReflectionMapping,
  FogExp2,
  HemisphereLight,
  type Scene,
} from 'three';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import { addSky } from './sky';

const DEFAULT_HDRI_URL = 'assets/hdri/kloofendal_48d_partly_cloudy_puresky_2k.hdr';

/**
 * Per-map atmosphere. Omitted → the default golden-hour HDRI look (the existing
 * maps are unchanged). Tribute maps set a palette to evoke a classic cheaply:
 * a flat dark `background` skips the HDRI entirely (a perf win) and a hemisphere
 * fill + tinted fog/sun give the mood — no extra render passes.
 */
export interface ScenePalette {
  /** 'hdri' uses the sky HDRI; a hex number is a flat background (void/cave). */
  background: 'hdri' | number;
  /** Override HDRI path (under public/). Omitted → the default golden-hour sky. */
  hdri?: string;
  fogColor: number;
  fogDensity?: number;
  sunColor: number;
  sunIntensity: number;
  /** HDRI env intensity, or (flat bg) the hemisphere fill intensity. */
  envIntensity: number;
  /** Hemisphere sky-fill colour for flat-background maps (defaults to fogColor). */
  ambientColor?: number;
}

/** One cached promise per HDRI url so switching maps doesn't re-decode. */
const hdriPromises = new Map<string, Promise<import('three').DataTexture | null>>();

function loadHdri(url: string): Promise<import('three').DataTexture | null> {
  let p = hdriPromises.get(url);
  if (!p) {
    p = new RGBELoader()
      .loadAsync(url)
      .then((texture) => {
        texture.mapping = EquirectangularReflectionMapping;
        return texture;
      })
      .catch(() => null);
    hdriPromises.set(url, p);
  }
  return p;
}

function addSun(scene: Scene, color: number, intensity: number): void {
  const sun = new DirectionalLight(color, intensity);
  sun.position.set(7000, 9000, 5000);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.left = -14000;
  sun.shadow.camera.right = 14000;
  sun.shadow.camera.top = 14000;
  sun.shadow.camera.bottom = -14000;
  sun.shadow.camera.near = 1000;
  sun.shadow.camera.far = 40000;
  sun.shadow.bias = -0.0004;
  sun.shadow.normalBias = 2;
  scene.add(sun);
}

/**
 * Shared per-map environment. Default (no palette): Poly Haven HDRI drives the
 * sky + IBL, warm raking sun, exponential haze. With a palette, applies the
 * tribute mood. Falls back to the gradient sky if the HDRI can't load.
 */
export async function addEnvironment(scene: Scene, palette?: ScenePalette): Promise<void> {
  if (palette && palette.background !== 'hdri') {
    // Flat-background tribute (void / cave): no HDRI, cheap hemisphere fill.
    scene.background = new Color(palette.background);
    scene.environment = null;
    scene.fog = new FogExp2(new Color(palette.fogColor), palette.fogDensity ?? 0.0001);
    scene.add(
      new HemisphereLight(palette.ambientColor ?? palette.fogColor, 0x141016, palette.envIntensity),
    );
    addSun(scene, palette.sunColor, palette.sunIntensity);
    return;
  }

  const hdri = await loadHdri(palette?.hdri ?? DEFAULT_HDRI_URL);
  if (hdri) {
    scene.environment = hdri;
    scene.background = hdri;
    scene.environmentIntensity = palette?.envIntensity ?? 0.62;
    scene.fog = new FogExp2(
      new Color(palette?.fogColor ?? 0xcdd6e0),
      palette?.fogDensity ?? 0.0001,
    );
    addSun(scene, palette?.sunColor ?? 0xffe6c2, palette?.sunIntensity ?? 2.6);
  } else {
    addSky(scene); // gradient + hemisphere + fog
    addSun(scene, palette?.sunColor ?? 0xffe6c2, palette?.sunIntensity ?? 2.8);
  }
}
